import { CAST_AI } from '@cast/constants';
import {
  Icons,
  notify,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import { WorkloadScalingPolicy } from '@cast/types';

import { PreparedWorkload } from 'features/workload-optimization';
import { useUpdateWorkloadMutationV2 } from 'hooks/mutations/workload-optimization/useUpdateWorkloadV2Mutation';

import { useConfirmationDialog } from './useConfirmationDialog';
import { isEnabledAutoAssignmentPolicy, isPolicyOptimized } from '../_utils';

type UseAutoAssignmentToggleParams = {
  workload: PreparedWorkload;
};

export const getDisableAutoAssignmentDialogConfig = ({
  isTheSameOptimizationStatus,
  isPolicyVpaOptimized,
}: {
  isTheSameOptimizationStatus: boolean;
  isPolicyVpaOptimized: boolean;
}) => {
  const title = isTheSameOptimizationStatus
    ? 'Disable automatic assignment?'
    : isPolicyVpaOptimized
    ? 'Enable automatic optimization?'
    : 'Disable automatic optimization?';

  const description = isTheSameOptimizationStatus
    ? 'The workload will be assigned to the policy manually. Assignments change only through direct updates. Automatic assignment can be re-enabled at any time.'
    : `${CAST_AI} will automatically adjust workload resource requests based on your policy settings. Some workloads may restart during optimization.`;

  const submitBtnLabel = isTheSameOptimizationStatus
    ? 'Disable'
    : isPolicyVpaOptimized
    ? 'Enable'
    : 'Disable';

  const icon = isTheSameOptimizationStatus ? (
    <Icons.ArrowsClockwise />
  ) : isPolicyVpaOptimized ? (
    <Icons.ToggleRight />
  ) : (
    <Icons.ToggleLeft />
  );

  const variant: 'notification' | 'check' = isTheSameOptimizationStatus
    ? 'notification'
    : isPolicyVpaOptimized
    ? 'check'
    : 'notification';

  return { title, description, submitBtnLabel, icon, variant };
};

export const useAutoAssignmentToggle = ({
  workload,
}: UseAutoAssignmentToggleParams) => {
  const { openConfirmationDialog, closeConfirmationDialog } =
    useConfirmationDialog();

  const { mutate, isPending } = useUpdateWorkloadMutationV2();

  // Auto-assignment is enabled when scalingPolicyOrigin is "assignment-rules"
  const isAutoAssignmentEnabled = isEnabledAutoAssignmentPolicy({
    workload,
  });

  const enableAutoAssignment = () => {
    openConfirmationDialog({
      name: workload.name,
      title: 'Enable automatic assignment?',
      body: 'The workload will be automatically assigned to its best matching policies. Assignments may change as rules or workload characteristics are updated.',
      variant: 'check',
      icon: <Icons.ArrowsClockwise />,
      confirmBtnLabel: 'Enable',
      confirmButtonProps: {
        onClick: () => {
          mutate(
            {
              clusterId: workload.clusterId,
              workloadId: workload.id,
              workload: {
                scalingPolicyId: null, // null enables auto-assignment
              },
            },
            {
              onSuccess: () => {
                notify.success('Automatic assignment enabled');
                closeConfirmationDialog();
              },
              onError: () => {
                closeConfirmationDialog();
              },
            }
          );
        },
      },
    });
  };

  const disableAutoAssignment = (newPolicy: WorkloadScalingPolicy) => {
    const isTheSameOptimizationStatus =
      newPolicy.recommendationPolicies.managementOption ===
      workload.workloadConfigV2.vpaConfig.managementOption;

    const isPolicyVpaOptimized = isPolicyOptimized(
      newPolicy.recommendationPolicies
    );

    const { title, description, submitBtnLabel, icon, variant } =
      getDisableAutoAssignmentDialogConfig({
        isTheSameOptimizationStatus,
        isPolicyVpaOptimized,
      });

    openConfirmationDialog({
      name: workload.name,
      title: title,
      body: (
        <>
          <Typography
            variant="P14R"
            color={tokens.theme.fg.structural.secondary}
          >
            {description}
          </Typography>
          {!isTheSameOptimizationStatus && (
            <Box
              sx={{
                mt: 12,
                borderRadius: '4px',
              }}
            >
              <Stack
                sx={{
                  m: '0 -65px',
                  backgroundColor: tokens.theme.bg.status.infoSubtle,
                  py: 12,
                }}
                direction="row"
                alignItems="center"
                justifyContent="center"
                color={tokens.theme.fg.status.onInfoSubtle}
                gap={8}
              >
                <Icons.Info weight="fill" />
                <Typography variant="P12M">
                  Switching from automatic policy assignment to manual
                  management.
                </Typography>
              </Stack>
            </Box>
          )}
        </>
      ),
      variant,
      icon: icon,
      confirmBtnLabel: submitBtnLabel,
      confirmButtonProps: {
        onClick: () => {
          mutate(
            {
              clusterId: workload.clusterId,
              workloadId: workload.id,
              workload: {
                scalingPolicyId: newPolicy.id,
              },
            },
            {
              onSuccess: () => {
                notify.success('Automatic assignment disabled');
                closeConfirmationDialog();
              },
              onError: () => {
                closeConfirmationDialog();
              },
            }
          );
        },
      },
    });
  };

  return {
    enableAutoAssignment,
    disableAutoAssignment,
    isAutoAssignmentEnabled,
    isLoading: isPending,
  };
};
